import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldCheck, BookOpen, Music, Play, Pause, PhoneCall, Minus, Plus, Lock, Facebook,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { GoldBadge, SectionHead, Bullets, Reveal } from "@/components/shared";
import PartnersSection from "@/components/PartnersSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "Court Support Request",
  "Advisory & Mentoring",
  "Community Safety Report",
  "General Enquiry",
  "Media & Born From Broken",
];

const TRACKS = [
  { title: "When a Child Speaks", artist: "P.A.I.N. Official Anthem", src: "/assets/when-a-child-speaks.mp3", testId: "track-when-a-child-speaks" },
  { title: "Born From Broken", artist: "P.A.I.N. Official Anthem", src: "/assets/born-from-broken.mp3", testId: "track-born-from-broken" },
  { title: "Architect of Ruins", artist: "P.A.I.N. Official Anthem", src: "/assets/architect-of-ruins.mp3", testId: "track-architect-of-ruins" },
];

const fmtTime = (s) => {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

const HOTLINES = [
  { name: "Emergency (Police / Ambulance)", number: "000" },
  { name: "Lifeline — Crisis Support", number: "13 11 14" },
  { name: "Kids Helpline", number: "1800 55 1800" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", category: CATEGORIES[0], message: "", anonymous: false });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [preOpen, setPreOpen] = useState(false);
  const [pre, setPre] = useState({ name: "", email: "", quantity: 1 });
  const [preSending, setPreSending] = useState(false);
  const [preDone, setPreDone] = useState(false);
  const [playing, setPlaying] = useState(null);
  const audioRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const toggleTrack = (t) => {
    if (!audioRef.current) {
      const audio = new Audio();
      audio.addEventListener("timeupdate", () => setProgress(audio.currentTime));
      audio.addEventListener("loadedmetadata", () => setDuration(audio.duration));
      audio.addEventListener("ended", () => {
        setPlaying(null);
        setProgress(0);
      });
      audioRef.current = audio;
    }
    const audio = audioRef.current;
    if (playing === t.testId) {
      audio.pause();
      setPlaying(null);
    } else {
      audio.src = t.src;
      setProgress(0);
      setDuration(0);
      audio.play().catch(() => toast.error("Could not play this track right now."));
      setPlaying(t.testId);
    }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message securely received. A P.A.I.N. advocate will respond.");
    } catch {
      toast.error("Message could not be sent. Please try again or call a crisis line below.");
    } finally {
      setSending(false);
    }
  };

  const submitPreorder = async (e) => {
    e.preventDefault();
    setPreSending(true);
    try {
      await axios.post(`${API}/preorders`, pre);
      setPreDone(true);
      toast.success("Pre-order reserved. Confirmation details will follow by email.");
    } catch {
      toast.error("Pre-order could not be placed. Please try again.");
    } finally {
      setPreSending(false);
    }
  };

  const inputCls =
    "w-full rounded-md border border-white/15 bg-[#0A0D14]/80 px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:border-amber-500/60 focus:outline-none focus:ring-1 focus:ring-amber-500/40 transition-colors";

  return (
    <div data-testid="contact-page">
      <section className="border-b border-white/10 bg-[#111622] grain-overlay relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <Reveal>
            <GoldBadge testId="contact-badge">Confidential • Survivor-Led • Secure</GoldBadge>
            <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-50">
              Call to Action <span className="gold-text-gradient">& Contact</span>
            </h1>
            <p className="mt-6 max-w-3xl text-base sm:text-lg leading-relaxed text-slate-300">
              Reach out for court support, access the Global Service Index, pre-order the
              autobiography, or stream the anthem. Every door below is open to you.
            </p>
            <a
              href="https://www.facebook.com/PAINAus"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-facebook-button"
              className="btn-gold-outline mt-6 inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold text-amber-300"
            >
              <Facebook className="h-4 w-4" />
              Follow P.A.I.N. on Facebook
            </a>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid gap-12 lg:grid-cols-[1fr_1.2fr]">
        <div className="space-y-10">
          <Reveal>
            <div data-testid="safe-portal-guidelines" className="card-pain p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-4">
                <Lock className="h-6 w-6 text-amber-400" />
                <h2 className="font-display text-xl sm:text-2xl font-medium text-slate-50">
                  Safe Portal Guidelines
                </h2>
              </div>
              <Bullets
                items={[
                  "Use a device and network that only you can access, where possible",
                  "You may submit anonymously — no name or email is required",
                  "For Court Support, include your hearing date and jurisdiction only if safe to do so",
                  "A trained survivor advocate — never an automated system — reads every message",
                  "If you are in immediate danger, call 000 before using this portal",
                ]}
              />
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div data-testid="crisis-hotlines-card" className="card-pain p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-4">
                <PhoneCall className="h-6 w-6 text-amber-400" />
                <h2 className="font-display text-xl sm:text-2xl font-medium text-slate-50">
                  24/7 Crisis Lines (Australia)
                </h2>
              </div>
              <ul className="space-y-3">
                {HOTLINES.map((h) => (
                  <li key={h.name} className="flex items-center justify-between gap-4 border-b border-white/5 pb-3 last:border-0 last:pb-0">
                    <span className="text-sm text-slate-300">{h.name}</span>
                    <span className="font-code text-sm font-bold text-amber-400">{h.number}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div data-testid="contact-form-card" className="card-pain p-6 sm:p-10">
            <div className="flex items-center gap-3 mb-2">
              <ShieldCheck className="h-6 w-6 text-amber-400" />
              <h2 className="font-display text-xl sm:text-2xl font-medium text-slate-50">
                Secure Survivor Contact Portal
              </h2>
            </div>
            <p className="text-sm text-slate-400 mb-2">
              Access any Global Service Index branch — Advisory & Mentoring, Advocacy & Court
              Support, or Community Safety Patrols — through this single secure channel.
            </p>
            <span
              data-testid="contact-acnc-badge"
              className="mb-8 inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full border border-amber-500/40 bg-white/95 text-slate-800 text-[10px] font-code uppercase tracking-wider font-semibold"
            >
              <img
                src="/assets/acnc-tick.jpg"
                alt="ACNC Registered Charity tick"
                className="h-5 w-5 rounded-full object-cover"
              />
              ACNC Registered Charity — ABN 95 161 193 853
            </span>

            {sent ? (
              <div data-testid="contact-success-panel" className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-8 text-center">
                <ShieldCheck className="mx-auto h-10 w-10 text-amber-400" />
                <h3 className="font-display mt-4 text-xl text-slate-50">Message Received</h3>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">
                  Your message has been securely logged. A P.A.I.N. advocate will respond to you
                  directly. You were heard.
                </p>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submitContact} className="space-y-5">
                <div>
                  <label htmlFor="contact-name" className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                    Name {form.anonymous ? "(withheld — anonymous)" : ""}
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    className={inputCls}
                    placeholder="Your name"
                    value={form.name}
                    disabled={form.anonymous}
                    required={!form.anonymous}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                    Email (optional)
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    data-testid="contact-email-input"
                    className={inputCls}
                    placeholder="you@example.com"
                    value={form.email}
                    disabled={form.anonymous}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </div>
                <div>
                  <label htmlFor="contact-category" className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                    I need
                  </label>
                  <select
                    id="contact-category"
                    data-testid="contact-category-select"
                    className={inputCls}
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">
                    Your message
                  </label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    className={`${inputCls} min-h-[140px] resize-y`}
                    placeholder="Share only what feels safe to share."
                    required
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                  />
                </div>
                <label className="flex items-center gap-3 text-sm text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="contact-anonymous-checkbox"
                    className="h-4 w-4 rounded border-white/20 bg-[#0A0D14] accent-amber-500"
                    checked={form.anonymous}
                    onChange={(e) => setForm({ ...form, anonymous: e.target.checked })}
                  />
                  Submit anonymously — withhold my name and email
                </label>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={sending}
                  className="btn-royal w-full rounded-md px-6 py-3 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {sending ? "Sending securely..." : "Send Secure Message"}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </section>

      {/* PRE-ORDER + ANTHEM */}
      <section className="border-t border-white/10 bg-[#111622] grain-overlay relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <Reveal>
            <SectionHead
              eyebrow="Born From Broken"
              title="Pre-Order the Autobiography. Stream the Anthem."
              description="Own the record of Ronald Waters' 45-year journey from state ward to Royal Commission victory — and hear it set to music."
            />
          </Reveal>
          <div className="mt-12 grid gap-8 lg:grid-cols-2">
            <Reveal>
              <div data-testid="preorder-card" className="card-pain p-6 sm:p-10 h-full">
                <BookOpen className="h-8 w-8 text-amber-400" />
                <h3 className="font-display mt-4 text-2xl font-semibold text-slate-50">
                  Born From Broken — The Autobiography
                </h3>
                <div className="mt-4">
                  <Bullets
                    items={[
                      "The complete 45-year account, from state ward to national record",
                      "Limited first-edition pre-order with signed copy option",
                      "All proceeds fund P.A.I.N. frontline programs",
                    ]}
                  />
                </div>
                <button
                  data-testid="preorder-open-button"
                  onClick={() => { setPreOpen(true); setPreDone(false); }}
                  className="btn-gold-outline mt-8 inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold text-amber-300"
                >
                  <BookOpen className="h-4 w-4" />
                  Pre-Order Now
                </button>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div data-testid="anthem-player-card" className="card-pain p-6 sm:p-10 h-full">
                <Music className="h-8 w-8 text-amber-400" />
                <h3 className="font-display mt-4 text-2xl font-semibold text-slate-50">
                  Companion Anthem Tracks
                </h3>
                <ul className="mt-6 space-y-3">
                  {TRACKS.map((t) => {
                    const isPlaying = playing === t.testId;
                    return (
                      <li
                        key={t.testId}
                        data-testid={t.testId}
                        className={`rounded-lg border p-4 transition-colors ${
                          isPlaying ? "border-amber-500/50 bg-amber-500/10" : "border-white/10 bg-[#0A0D14]/60"
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <button
                            data-testid={`${t.testId}-play-button`}
                            onClick={() => toggleTrack(t)}
                            className="btn-royal flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white"
                            aria-label={isPlaying ? `Pause ${t.title}` : `Play ${t.title}`}
                          >
                            {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
                          </button>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-slate-100">{t.title}</p>
                            <p className="truncate text-xs text-slate-400">{t.artist}</p>
                          </div>
                          {isPlaying && (
                            <div className="flex items-end gap-[3px] h-5" aria-hidden="true">
                              {[0, 1, 2, 3].map((b) => (
                                <span
                                  key={b}
                                  className="eq-bar w-[3px] h-full bg-amber-400 rounded-sm"
                                  style={{ animationDelay: `${b * 0.15}s` }}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                        {isPlaying && (
                          <div className="mt-3" data-testid={`${t.testId}-progress`}>
                            <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-amber-500 to-amber-300 transition-[width] duration-300"
                                style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }}
                              />
                            </div>
                            <div className="mt-1.5 flex justify-between font-code text-[10px] text-slate-500">
                              <span>{fmtTime(progress)}</span>
                              <span>{fmtTime(duration)}</span>
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
                <p className="mt-4 text-xs text-slate-500">
                  Official P.A.I.N. anthem recordings — press play to listen right here.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <PartnersSection />

      <Dialog open={preOpen} onOpenChange={setPreOpen}>
        <DialogContent data-testid="preorder-dialog" className="bg-[#111622] border-amber-500/30 text-slate-100 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-slate-50">
              Pre-Order: Born From Broken
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">
              Reserve your copy of Ronald Waters' official autobiography. No payment is taken today.
            </DialogDescription>
          </DialogHeader>
          {preDone ? (
            <div data-testid="preorder-success-panel" className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-6 text-center">
              <BookOpen className="mx-auto h-8 w-8 text-amber-400" />
              <p className="mt-3 text-sm text-slate-200 font-semibold">Your pre-order is reserved.</p>
              <p className="mt-1 text-xs text-slate-400">
                {pre.quantity} {pre.quantity === 1 ? "copy" : "copies"} held for {pre.name}. Confirmation will follow by email.
              </p>
            </div>
          ) : (
            <form data-testid="preorder-form" onSubmit={submitPreorder} className="space-y-4">
              <input
                data-testid="preorder-name-input"
                className={inputCls}
                placeholder="Full name"
                required
                value={pre.name}
                onChange={(e) => setPre({ ...pre, name: e.target.value })}
              />
              <input
                type="email"
                data-testid="preorder-email-input"
                className={inputCls}
                placeholder="Email address"
                required
                value={pre.email}
                onChange={(e) => setPre({ ...pre, email: e.target.value })}
              />
              <div className="flex items-center justify-between rounded-md border border-white/15 bg-[#0A0D14]/80 px-4 py-2.5">
                <span className="text-sm text-slate-300">Quantity</span>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    data-testid="preorder-quantity-decrease"
                    className="p-1.5 rounded-md border border-white/15 text-slate-300 hover:border-amber-500/50 hover:text-amber-300 transition-colors"
                    onClick={() => setPre({ ...pre, quantity: Math.max(1, pre.quantity - 1) })}
                    aria-label="Decrease quantity"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span data-testid="preorder-quantity-display" className="font-code text-sm font-bold text-amber-400 w-6 text-center">
                    {pre.quantity}
                  </span>
                  <button
                    type="button"
                    data-testid="preorder-quantity-increase"
                    className="p-1.5 rounded-md border border-white/15 text-slate-300 hover:border-amber-500/50 hover:text-amber-300 transition-colors"
                    onClick={() => setPre({ ...pre, quantity: Math.min(50, pre.quantity + 1) })}
                    aria-label="Increase quantity"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <button
                type="submit"
                data-testid="preorder-confirm-button"
                disabled={preSending}
                className="btn-royal w-full rounded-md px-6 py-3 text-sm font-semibold text-white disabled:opacity-60"
              >
                {preSending ? "Reserving..." : "Confirm Pre-Order"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
